import jwtDecode from "jwt-decode";
import localStorageService from "@/services/localStorage";
import * as types from "./mutation-types";
import api from "../../../api";
import { Auth } from "aws-amplify";
import { ParticipantPortal } from "@/components/services/participant-service";
import { AWSAmplify } from "../../../services/cofigureAWSAmplify";
import { datadogRum } from "@datadog/browser-rum";
import _ from "lodash";
import eventBus from "@/eventBus";

const cognitoUserSession = async function() {
  return new Promise(async (resolve, reject) => {
    try {
      const session = await Auth.currentSession();
      resolve(session);
    } catch (e) {
      reject("Access token not available. Seems your account is not fully verified.");
    }
  });
};

export const awsCognitoLogin = async ({ dispatch }, { password, username, electronicSignatureSignIn }) => {
  try {
    const clientId = localStorageService.getItem("clientId");
    const userIPAddress = await getUserIPAddress();
    await Auth.signIn(username.toLowerCase(), password, { userType: "portal", requestFor: "SIGNIN", clientId: clientId , loginType:"NORMAL", ipAddress: userIPAddress });
    const currentUserSession = await cognitoUserSession();
    localStorageService.setItem("accessToken_benchmark", currentUserSession.accessToken.jwtToken);
    localStorageService.setItem("idToken_benchmark", currentUserSession.idToken.jwtToken);
    const awsToken = currentUserSession.idToken.jwtToken;
    dispatch("setAWSAccessTokens", {
      awsToken
    });

    if (electronicSignatureSignIn) {
      return;
    }

    //Send this AWSToken to get the JAVA compatible token.
    try {
      const response = await api.getJavaCompatibleAccessToken({ strictWebLogin: true });
      if (response.body.tokenDto.next === "TwoFARequest") {
        return new Promise(resolve => {
          resolve(response.body.tokenDto);
        });
      } else if (response.body && (response.body.tokenDto.next === "OTPVerifiedRequest" || (response.body.tokenDto.token && response.body.tokenDto.refreshToken))) {
        const { token, refreshToken } = response.body.tokenDto;
        dispatch("userSetTokens", {
          token,
          refreshToken
        });
        const tokenObj = jwtDecode(token);
        await dispatch("userLoadProfile", tokenObj.id);
      }
    } catch (error) {
      await Auth.signOut();
      throw new Error(error.body.message);
    }
  } catch (error) {
    let errorMsg = "";
    switch (error.code) {
      case "UserNotFoundException":
        errorMsg = "Username not found.";
        break;
      case "NotAuthorizedException":
        //call an api to update the failed login attempts in DB
        try {
          const res = await api.updateFailedLoginAttempt({ username: username.toLowerCase() });
          errorMsg = res.body.message;
        } catch (error) {
          errorMsg = error.body.message;
        }
        break;
      default:
        if (error.message) {
          let errMsg = error.message.split("###");
          errorMsg = errMsg.length > 1 ? errMsg[2].trim() : errMsg[0];
        } else {
          errorMsg = error;
        }
    }
    throw new Error(errorMsg);
  }
};
export const userLogin = async ({ dispatch, getters }, { password, username, electronicSignatureSignIn }) => {
  try {
    if (electronicSignatureSignIn) {
      electronicSignatureSignIn = electronicSignatureSignIn === "YES"; // eslint-disable-line no-param-reassign
    }
    //Based on the Authentication flow and electronicSignatureSignIn we need to call the awsCognitoLogin action
    if (getters.awsCognitoAuthentication && getters.awsCognitoAuthentication === true && electronicSignatureSignIn) {
      await awsCognitoLogin({ dispatch }, { password, username, electronicSignatureSignIn });
    } else {
      const response = await api.loginUserFact({ password, username: username.toLowerCase(), electronicSignatureSignIn });
      if (response.body && response.body.result.next === "TwoFARequest") {
        return new Promise(resolve => {
          resolve(response.body.result);
        });
      } else if (response.body && (response.body.result.next === "OTPVerifiedRequest" || (response.body.result.token && response.body.result.refreshToken))) {
        if (response.body && response.body.result) {
          const { token, refreshToken } = response.body.result;
          if (token && (electronicSignatureSignIn == undefined || !electronicSignatureSignIn)) {
            dispatch("userSetTokens", {
              token,
              refreshToken
            });

            const tokenObj = jwtDecode(token);
            await dispatch("userLoadProfile", tokenObj.id);
          }
        }
      }
    }
  } catch (error) {
    const errorMsg = error.body && error.body.message ? error.body.message : error.message ? error.message : "You have entered an invalid username or password";
    throw new Error(errorMsg);
  }
};

export const checkifSchedulesGenerated = (context, { pid, studyId, versionId }) => {
  localStorage.removeItem("checkSchedule");
  return new Promise((resolve, reject) => {
    let payload = { pid, studyId, versionId };
    let intervalHandler = () => {
      api
        .isTaskScheduleCreatedForNewStudyVersion(payload)
        .then(res => {
          const status = res.data.status;
          if (status === "INPROGRESS") {
            //Loader is showing globally, do nothing
            intervalHandler();
          } else if (status === "SUCCEEDED") {
            resolve(res);
          } else {
            reject("Internal Server Error");
          }
        })
        .catch(err => {
          reject(err);
        });
    };
    setTimeout(intervalHandler, 1000);
  });
};

export const awsCognitoParticipantLogin = async ({ dispatch }, data) => {
  let portal = new ParticipantPortal();
  try {
    const { password, email, studyId, country } = data;
    await Auth.signIn(email, password, { userType: "Participant_Web", requestFor: "SIGNIN", studyId, countryId: country });
    const currentUserSession = await cognitoUserSession();
    const participantAWSToken = currentUserSession.idToken.jwtToken;
    const participantAccessToken = currentUserSession.accessToken.jwtToken;
    dispatch("setAWSAccessTokenForParticipant", {
      participantAWSToken,
      participantAccessToken
    });

    //Send this AWSToken to get the JAVA compatible token.
    try {
      const response = await api.getParticipantJavaCompatibleAccessToken(data);
      const { accesstoken, refreshtoken } = response.body;
      dispatch("participantSetTokens", {
        accesstoken,
        refreshtoken
      });
      const tokenObj = jwtDecode(accesstoken);
      localStorageService.setItem("pw-studyId", tokenObj.studyId);
      await dispatch("participantLoadProfile", tokenObj.pId);
    } catch (error) {
      eventBus.$emit("isDisabledLoginButton", false);
      await Auth.signOut();
      throw new Error(error.body.message);
    }
  } catch (error) {
    eventBus.$emit("isDisabledLoginButton", false);
    if (!Object.keys(error).includes("__type")) {
      error.__type = error.name;
    }
    let err = portal.cognitoValidations(error);
    throw new Error(err.message);
  }
};

export const participantLogin = async ({ dispatch, getters }, data) => {
  try {
    if (getters.pwebCognitoAuthentication && getters.pwebCognitoAuthentication === true && data.electronicSignatureSignIn) {
      await awsCognitoParticipantLogin({ dispatch }, data);
    } else {
      const response = await api.loginParticipantFact(data);
      if (response && response.body) {
        const { accesstoken, refreshtoken } = response.body;
        if (accesstoken) {
          dispatch("participantSetTokens", { accesstoken, refreshtoken });
          const tokenObj = jwtDecode(accesstoken);
          await dispatch("participantLoadProfile", tokenObj.pId);
        }
      }
    }
  } catch (error) {
    const errorMsg = error.body && error.body.message ? error.body.message : "You have entered an invalid username or password";
    throw new Error(errorMsg);
  }
};

export const SSOLogin = async ({ dispatch }, awsToken) => {
  try {
    dispatch("setAWSAccessTokens", {
      awsToken
    });
    //Send this AWSToken to get the JAVA compatible token.
    try {
      const response = await api.getJavaCompatibleAccessToken({ isSSOLogin: true, strictWebLogin: true });
      const { token, refreshToken } = response.body.tokenDto;
      dispatch("userSetTokens", {
        token,
        refreshToken
      });
      const tokenObj = jwtDecode(token);
      await dispatch("userLoadProfile", tokenObj.id);
    } catch (error) {
      throw error.body.message;
    }
  } catch (error) {
    let errorMsg = "";
    if (error.code) {
      switch (error.code) {
        case "UserNotFoundException":
          errorMsg = "Username not found.";
          break;
        default:
          if (error.message) {
            let errMsg = error.message.split("###");
            errorMsg = errMsg.length > 1 ? errMsg[2].trim() : errMsg[0];
          } else {
            errorMsg = error;
          }
      }
    } else {
      errorMsg = error;
    }
    throw errorMsg;
  }
};

export const userLoadProfile = async ({ dispatch }, userId) => {
  try {
    const response = await api.getProfileDetails(userId);
    const user = response.body;
    dispatch("userUpdate", user);
    eventBus.$emit("isDisabledLoginButton", false);
  } catch (error) {
    eventBus.$emit("isDisabledLoginButton", false);
    const errorMsg = error.body && error.body.message ? error.body.message : "Error while getting the profile details";
    throw new Error(errorMsg);
  }
};

export const participantLoadProfile = async ({ dispatch }, userId) => {
  try {
    const response = await api.getParticipantsProfileDetails(userId);
    const user = response.body;
    if (localStorage.getItem("checkSchedule")) {
      await dispatch("checkifSchedulesGenerated", {
        pid: user.pid,
        studyId: user.studyId,
        versionId: user.studyVersionId
      });
    }
    dispatch("participantUpdate", user);
    eventBus.$emit("isDisabledLoginButton", false);
  } catch (error) {
    eventBus.$emit("isDisabledLoginButton", false);
    const errorMsg = error.body && error.body.message ? error.body.message : "Error while getting the profile details";
    throw new Error(errorMsg);
  }
};

export const setupLocalStorageUserData = async ({ commit, dispatch }) => {
  const user = localStorageService.getItem("userObj");
  const token = localStorageService.getItem("token");
  const refreshToken = localStorageService.getItem("refreshToken");
  const clientId = localStorageService.getItem("clientId");
  const clientName = localStorageService.getItem("clientName");
  const awsCognitoAuthentication = localStorageService.getItem("awsCognitoAuthentication");
  const pwebCognitoAuthentication = localStorageService.getItem("pwebCognitoAuthentication");

  if (clientId) {
    dispatch("clientIdSuccess", clientId);
  }
  if (clientName) {
    dispatch("clientName", clientName);
  }
  if (user) {
    if (user.pid && user.pid !== "") {
      if (process.env.VUE_APP_DD_WEB_PORTAL_APP_ID && process.env.VUE_APP_DD_WEB_PORTAL_APP_ID != "") {
        datadogRum.setUser({ id: user.pid, name: user.userDefinedParticipantId });
        datadogRum.addRumGlobalContext("studyId", user.studyId);
        datadogRum.addRumGlobalContext("studyVersionId", user.studyVersionId);
      }
      commit(types.SET_PARTICIPANT, user);
    } else {
      datadogRum.setUser({ id: user.id });
      commit(types.SET_USER, user);
    }
  }
  if (token && refreshToken) {
    if (user && user.pid) {
      const accesstoken = token;
      const refreshtoken = refreshToken;
      commit(types.SET_PARTICIPANT_TOKENS, { accesstoken, refreshtoken });
    } else {
      commit(types.SET_USER_TOKENS, { token, refreshToken });
    }
  }
  if (awsCognitoAuthentication && awsCognitoAuthentication == "true") {
    dispatch("setAwsAuthenticationFlow", true);
    //Check whether PwebCognitoflag is true
    if (pwebCognitoAuthentication && pwebCognitoAuthentication == "true") {
      dispatch("setPWEBCognitoFlag", true);
    } else {
      dispatch("setPWEBCognitoFlag", false);
    }

    if (window.location.href.includes("/participants/web/") && pwebCognitoAuthentication && pwebCognitoAuthentication == "true") {
      let amplify = new AWSAmplify();
      await amplify.configureAWSAmplifyParticipant();
    } else {
      let amplify = new AWSAmplify();
      await amplify.configureAWSAmplify();
    }
  } else {
    dispatch("setAwsAuthenticationFlow", false);
    dispatch("setPWEBCognitoFlag", false);
  }
};

export const userUpdate = async ({ commit }, user) => {
  commit(types.SET_USER, user);
  const obj = { ...user };
  let userObj = _.omit(obj, ["username", "firstName", "lastName", "name", "phoneNumber", "workNumber"]);
  localStorageService.setItem("userObj", userObj);
};

export const participantUpdate = async ({ commit }, user) => {
  commit(types.SET_PARTICIPANT, user);
  localStorageService.setItem("userObj", user);
};

export const participantSetTokens = async ({ commit }, { accesstoken, refreshtoken }) => {
  commit(types.SET_PARTICIPANT_TOKENS, { accesstoken, refreshtoken });
  localStorageService.setItem("token", accesstoken);
  localStorageService.setItem("refreshToken", refreshtoken);
};

export const userSetTokens = async ({ commit }, { token, refreshToken }) => {
  commit(types.SET_USER_TOKENS, { token, refreshToken });
  localStorageService.setItem("token", token);
  localStorageService.setItem("refreshToken", refreshToken);
};

export const setAWSAccessTokens = async ({ commit }, { awsToken }) => {
  commit(types.SET_AWS_ACCESS_TOKENS, { awsToken });
  localStorageService.setItem("awsToken", awsToken);
};

export const setAWSAccessTokenForParticipant = async ({ commit }, { participantAWSToken, participantAccessToken }) => {
  commit(types.SET_PARTICIPANT_AWS_ACCESS_TOKEN, { participantAWSToken });
  localStorageService.setItem("participantAWSToken", participantAWSToken);
  localStorageService.setItem("participantAccessToken", participantAccessToken);
};

export const userLogout = async ({ commit, getters }) => {
  if (process.env.VUE_APP_DD_WEB_PORTAL_APP_ID && process.env.VUE_APP_DD_WEB_PORTAL_APP_ID != "") {
    datadogRum.removeUser();
  }
  commit(types.LOGOUT_USER);
  const studySummaryStatus = localStorageService.getItem("studySummary");
  localStorageService.removeAll();
  localStorageService.setItem("studySummary", studySummaryStatus);
  localStorageService.setItem("logOut", true);
  if (getters.awsCognitoAuthentication && getters.awsCognitoAuthentication === true) {
    await Auth.signOut();
  }
  if (getters.token && !getters.isUserSessionExpired) {
    document.location.reload();
  }
};
export const userSessionExpired = ({ commit }, status = true) => {
  commit(types.SESSION_EXPIRED, status);
};
export const participantLogout = async ({ commit, getters }) => {
  if (process.env.VUE_APP_DD_WEB_PORTAL_APP_ID && process.env.VUE_APP_DD_WEB_PORTAL_APP_ID != "") {
    datadogRum.removeUser();
  }
  commit(types.LOGOUT_PARTICIPANT);
  const studySummaryStatus = localStorageService.getItem("studySummary");
  let countryId = localStorageService.getItem("pw-countryId");
  let languageId = localStorageService.getItem("pw-languageId");
  const awsCognitoAuthentication = localStorageService.getItem("awsCognitoAuthentication");
  const pwebCognitoAuthentication = localStorageService.getItem("pwebCognitoAuthentication");

  localStorageService.removeAll();
  localStorageService.setItem("pw-countryId", countryId);
  localStorageService.setItem("pw-languageId", languageId);
  localStorageService.setItem("awsCognitoAuthentication", awsCognitoAuthentication);
  localStorageService.setItem("pwebCognitoAuthentication", pwebCognitoAuthentication);

  localStorageService.setItem("studySummary", studySummaryStatus);
  localStorageService.setItem("logOut", true);

  if (getters.pwebCognitoAuthentication && getters.pwebCognitoAuthentication === true) {
    await Auth.signOut();
  }

  if (getters.token && !getters.isUserSessionExpired) {
    document.location.reload();
  }
};

export const sessionExpiredLogoutUser = async ({ commit, getters }) => {
  if (process.env.VUE_APP_DD_WEB_PORTAL_APP_ID && process.env.VUE_APP_DD_WEB_PORTAL_APP_ID != "") {
    datadogRum.removeUser();
  }
  commit(types.LOGOUT_USER);
  const studySummaryStatus = localStorageService.getItem("studySummary");
  const awsCognitoAuthentication = localStorageService.getItem("awsCognitoAuthentication");
  localStorageService.removeAll();
  localStorageService.setItem("studySummary", studySummaryStatus);
  localStorageService.setItem("awsCognitoAuthentication", awsCognitoAuthentication);
  localStorageService.setItem("logOut", true);
  if (getters.awsCognitoAuthentication && getters.awsCognitoAuthentication === true) {
    await Auth.signOut();
  }
};

export const saveParticipantRegistration = async ({ commit }, data) => {
  commit(types.SAVE_PARTICIPANT_DATA, data);
};

export const saveParticipantStudyStartStatus = async ({ commit }, data) => {
  commit(types.SAVE_PARTICIPANT_STUDY_START_STATUS, data);
};

async function getUserIPAddress() {
  try {
    const response = await fetch("https://api.ipify.org?format=json");
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error("Error while getting IP address");
    return null;
  }
}
